// YourAccount.jsx
"use client";

import { useContext, useState, useRef, useMemo } from "react";
import imageCompression from "browser-image-compression";
import { AuthContext } from "../../../context/AuthContext";
import { useAlert } from "context/AlertContext";
import { uploadSingleFile } from "services/upload-service/upload-service";
import LocalDateTime from "services/date-converter";
import { formatTrades } from "utils/trade-format";
import apiAccount from "./api";

import {
  FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCity, FaIdCard,
  FaBriefcase, FaEdit, FaTimes, FaCamera, FaSave, FaGlobe,
  FaBuilding, FaUserTag, FaLock, FaVenusMars, FaSignInAlt,
  FaShieldAlt, FaCalendarAlt, FaCheckCircle, FaLayerGroup,
} from "react-icons/fa";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const PROFILE_IMAGE = {
  maxSizeMB: 10,
  convertSizeKB: 50,
  acceptedTypes: ["image/jpeg", "image/png", "image/webp"],
};

const EDITABLE_FIELDS = ["first_name", "last_name", "gender", "phone", "location", "city", "pin"];

const buildFormState = (user) => ({
  first_name: user?.first_name || "",
  last_name:  user?.last_name  || "",
  gender:     user?.gender     || "",
  phone:      user?.phone      || "",
  location:   user?.location   || "",
  city:       user?.city       || "",
  pin:        user?.pin        ?? "",
});

// ─────────────────────────────────────────────────────────────────────────────
// Shared class strings
// ─────────────────────────────────────────────────────────────────────────────
const inputCls =
  "w-full px-3 py-2 text-sm border border-border rounded-lg bg-transparent text-text_primary " +
  "placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors";

const readViewCls =
  "text-sm text-text_primary px-3 py-2 bg-surface_alt rounded-lg min-h-[38px] flex items-center";

// ─────────────────────────────────────────────────────────────────────────────
// Field components — defined at module scope to avoid losing focus on re-render
// ─────────────────────────────────────────────────────────────────────────────
const EditableField = ({ icon: Icon, label, name, type = "text", placeholder, required, fullWidth, isEditing, formData, onChange }) => (
  <div className={fullWidth ? "sm:col-span-2" : ""}>
    <label className="block text-xs font-medium text-text_secondary mb-1.5 flex items-center gap-1.5">
      <Icon size={11} className="text-text_muted shrink-0" />
      {label}
      {required && <span className="text-danger ml-0.5">*</span>}
    </label>
    {isEditing ? (
      <input
        type={type}
        name={name}
        value={formData[name] ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className={inputCls}
      />
    ) : (
      <p className={readViewCls}>
        {formData[name] || <span className="text-text_muted italic">Not added</span>}
      </p>
    )}
  </div>
);

const ReadOnlyField = ({ icon: Icon, label, value, badge }) => (
  <div>
    <label className="block text-xs font-medium text-text_secondary mb-1.5 flex items-center gap-1.5">
      <Icon size={11} className="text-text_muted shrink-0" />
      {label}
      <FaLock size={8} className="text-border ml-auto" title="Read only" />
    </label>
    <div className="text-sm text-text_primary px-3 py-2 bg-surface_alt rounded-lg min-h-[38px] flex items-center justify-between gap-2 border border-border/40">
      <span className="truncate">{value}</span>
      {badge}
    </div>
  </div>
);

const SelectGender = ({ isEditing, formData, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-text_secondary mb-1.5 flex items-center gap-1.5">
      <FaVenusMars size={11} className="text-text_muted shrink-0" />
      Gender
    </label>
    {isEditing ? (
      <select name="gender" value={formData.gender ?? ""} onChange={onChange} className={`${inputCls} bg-surface`}>
        <option value="">Select gender</option>
        <option value="MALE">Male</option>
        <option value="FEMALE">Female</option>
      </select>
    ) : (
      <p className={`${readViewCls} capitalize`}>
        {formData.gender ? formData.gender.toLowerCase() : <span className="text-text_muted italic">Not added</span>}
      </p>
    )}
  </div>
);

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
    {/* Top accent */}
    <div className="h-[3px] bg-gradient-to-r from-primary to-secondary/60" />
    <div className="p-5">
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-8 h-8 rounded-lg bg-primary/[0.09] flex items-center justify-center shrink-0 ring-1 ring-primary/10">
          <Icon size={13} className="text-primary" />
        </div>
        <div>
          <h3 className="text-[13px] font-semibold text-text_primary">{title}</h3>
          {subtitle && <p className="text-[11px] text-text_muted mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────
export default function YourAccount() {
  const { user, refreshProfile } = useContext(AuthContext);
  const { showAlert }            = useAlert();
  const fileInputRef             = useRef(null);

  const [isEditing,       setIsEditing]       = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [uploadingImage,  setUploadingImage]  = useState(false);
  const [uploadProgress,  setUploadProgress]  = useState(0);
  const [formData,        setFormData]        = useState(() => buildFormState(user));

  const readOnly = useMemo(() => ({
    email:           user?.email                           || "—",
    company:         user?.company?.company_name           || "—",
    companyLogo:     user?.company?.company_url            || null,
    designation:     user?.designation?.designation_name  || "—",
    designationCode: user?.designation?.designation_code  || "",
    trade:           formatTrades(user?.trade_of_works)     || "—",
    businessType:    user?.business_type?.name             || "",
    country:         user?.country_directory?.country_name || "—",
    role:            user?.user_role?.role                 || "USER",
    isApproved:      !!user?.is_approved,
    isActive:        !!user?.is_activated,
    loginCount:      user?.login_count                     ?? 0,
    lastLogin:       user?.last_login_date,
    memberSince:     user?.first_login_date || user?.created_date,
  }), [user]);

  const profileImage = user?.profile_picture_url || null;
  const avatarColor  = user?.colour_code || "var(--color-primary)";

  const getInitials = () =>
    ((formData.first_name?.[0] || "") + (formData.last_name?.[0] || "")).toUpperCase() || "U";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData(buildFormState(user));
    setIsEditing(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!PROFILE_IMAGE.acceptedTypes.includes(file.type)) {
      showAlert({ type: "error", message: "Please upload a JPG, PNG or WEBP image." });
      return;
    }
    if (file.size > PROFILE_IMAGE.maxSizeMB * 1024 * 1024) {
      showAlert({ type: "error", message: `Image must be under ${PROFILE_IMAGE.maxSizeMB} MB.` });
      return;
    }

    setUploadingImage(true);
    setUploadProgress(0);

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: PROFILE_IMAGE.convertSizeKB / 1024,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      });

      const result = await uploadSingleFile({
        file: compressed,
        directory: "profile-images",
        projectTitle: formData.first_name || "profile",
        onProgress: setUploadProgress,
      });

      if (!result.success) {
        showAlert({ type: "error", message: "Image upload failed. Please try again." });
        return;
      }

      const res = await apiAccount.updateProfile(user.id, { profile_picture_url: result.file_url });
      if (res?.data?.status) {
        await refreshProfile?.();
        showAlert({ type: "success", message: "Profile photo updated." });
      } else {
        showAlert({ type: "warning", message: res?.data?.message || "Could not save photo." });
      }
    } catch {
      showAlert({ type: "error", message: "Image processing failed. Please try a different image." });
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    if (!formData.first_name.trim()) {
      showAlert({ type: "warning", message: "First name is required." });
      return;
    }
    if (formData.phone && !/^\d{10}$/.test(String(formData.phone))) {
      showAlert({ type: "warning", message: "Phone number must be 10 digits." });
      return;
    }

    const payload = EDITABLE_FIELDS.reduce((acc, key) => {
      acc[key] = formData[key] === "" ? null : formData[key];
      return acc;
    }, {});

    try {
      setSaving(true);
      const res = await apiAccount.updateProfile(user.id, payload);
      if (res?.data?.status) {
        await refreshProfile?.();
        setIsEditing(false);
        showAlert({ type: "success", message: "Profile updated successfully." });
      } else {
        showAlert({ type: "warning", message: res?.data?.message || "Update failed." });
      }
    } catch (err) {
      showAlert({ type: "error", message: err?.response?.data?.message || "An unexpected error occurred." });
    } finally {
      setSaving(false);
    }
  };

  const fieldProps = { isEditing, formData, onChange: handleInputChange };

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="max-w-[100rem] mx-auto w-full py-3">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-primary">Your Account</h1>
            <p className="text-sm text-text_secondary mt-0.5">Manage your personal information and profile settings</p>
          </div>

          <div className="flex items-center gap-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary_hover transition-colors shadow-sm"
              >
                <FaEdit size={13} /> Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-text_secondary bg-surface border border-border hover:bg-surface_alt transition-colors disabled:opacity-50"
                >
                  <FaTimes size={12} /> Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-secondary hover:bg-secondary_hover transition-colors shadow-sm disabled:opacity-60"
                >
                  <FaSave size={13} />
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Layout ──────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">

          {/* ── Sidebar ─────────────────────────────────────────────────── */}
          <div className="lg:sticky lg:top-5 lg:self-start">
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">

              {/* Cover band */}
              <div className="h-20 bg-gradient-to-r from-primary to-secondary relative overflow-hidden">
                <div className="absolute inset-0 opacity-10"
                  style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,.15) 8px, rgba(255,255,255,.15) 9px)" }} />
              </div>

              {/* Avatar */}
              <div className="flex justify-center -mt-10 pb-0">
                <div className="relative">
                  <div
                    className="w-20 h-20 rounded-full border-[3px] border-surface shadow-md flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: avatarColor }}
                  >
                    {profileImage
                      ? <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                      : <span className="text-white text-xl font-bold select-none">{getInitials()}</span>
                    }
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-1">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {uploadProgress > 0 && <span className="text-white text-[9px] font-bold">{uploadProgress}%</span>}
                      </div>
                    )}
                  </div>
                  <input type="file" ref={fileInputRef} className="hidden"
                    accept={PROFILE_IMAGE.acceptedTypes.join(",")} onChange={handleImageUpload} />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    title="Change photo"
                    className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-surface border border-border flex items-center justify-center text-primary shadow-sm hover:bg-surface_alt transition-colors disabled:opacity-50"
                  >
                    <FaCamera size={11} />
                  </button>
                </div>
              </div>

              {/* Name / designation / chips */}
              <div className="px-4 pt-3 pb-4 text-center">
                <h2 className="text-sm font-bold text-primary">
                  {formData.first_name} {formData.last_name}
                </h2>
                <p className="text-xs text-text_muted mt-0.5">{readOnly.designation}</p>

                <div className="mt-2 flex items-center justify-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-secondary">
                    <FaShieldAlt size={7} /> {readOnly.role}
                  </span>
                  {readOnly.isActive ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success_bg text-success border border-success_border">
                      <span className="w-1 h-1 rounded-full bg-success" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-danger_bg text-danger border border-danger_border">
                      <span className="w-1 h-1 rounded-full bg-danger" /> Inactive
                    </span>
                  )}
                </div>

                {/* Quick info */}
                <div className="mt-4 pt-3 border-t border-border space-y-2 text-left">
                  {[
                    { Icon: FaEnvelope,     val: readOnly.email              },
                    { Icon: FaPhone,        val: formData.phone || "—"       },
                    { Icon: FaBuilding,     val: readOnly.company            },
                    { Icon: FaMapMarkerAlt, val: formData.city  || "—"       },
                    { Icon: FaGlobe,        val: readOnly.country            },
                  ].map(({ Icon, val }, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs min-w-0">
                      <Icon size={10} className="text-text_muted shrink-0" />
                      <span className="text-text_secondary truncate">{val}</span>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-2">
                  <div className="bg-surface_alt rounded-lg p-2 text-left">
                    <p className="text-[9px] uppercase tracking-wide text-text_muted">Logins</p>
                    <p className="text-sm font-bold text-primary mt-0.5">{readOnly.loginCount}</p>
                  </div>
                  <div className="bg-surface_alt rounded-lg p-2 text-left">
                    <p className="text-[9px] uppercase tracking-wide text-text_muted">Approval</p>
                    <p className={`text-sm font-bold mt-0.5 ${readOnly.isApproved ? "text-success" : "text-warning"}`}>
                      {readOnly.isApproved ? "Approved" : "Pending"}
                    </p>
                  </div>
                  <div className="col-span-2 bg-surface_alt rounded-lg p-2 text-left">
                    <p className="text-[9px] uppercase tracking-wide text-text_muted flex items-center gap-1">
                      <FaSignInAlt size={7} /> Last login
                    </p>
                    <p className="text-xs font-medium text-text_primary mt-0.5 truncate">
                      {readOnly.lastLogin ? <LocalDateTime utcTime={readOnly.lastLogin} /> : "Never"}
                    </p>
                  </div>
                  <div className="col-span-2 bg-surface_alt rounded-lg p-2 text-left">
                    <p className="text-[9px] uppercase tracking-wide text-text_muted flex items-center gap-1">
                      <FaCalendarAlt size={7} /> Member since
                    </p>
                    <p className="text-xs font-medium text-text_primary mt-0.5">
                      {readOnly.memberSince ? <LocalDateTime utcTime={readOnly.memberSince} type="date" /> : "—"}
                    </p>
                  </div>
                </div>

                {readOnly.isApproved && (
                  <div className="mt-3 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-success_bg border border-success_border">
                    <FaCheckCircle size={10} className="text-success" />
                    <span className="text-[10.5px] font-semibold text-success">Account verified</span>
                  </div>
                )}

                <p className="text-[10px] text-text_muted mt-3">
                  JPG · PNG · WEBP — compressed to ~{PROFILE_IMAGE.convertSizeKB}KB
                </p>
              </div>
            </div>
          </div>

          {/* ── Form sections ───────────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Basic Information */}
            <SectionCard icon={FaUser} title="Basic Information" subtitle="Your personal details">
              <EditableField {...fieldProps} icon={FaUser} label="First Name" name="first_name" placeholder="John" required />
              <EditableField {...fieldProps} icon={FaUser} label="Last Name"  name="last_name"  placeholder="Doe" />
              <SelectGender {...fieldProps} />
            </SectionCard>

            {/* Contact */}
            <SectionCard icon={FaPhone} title="Contact Details" subtitle="How we reach you">
              <ReadOnlyField icon={FaEnvelope} label="Email Address" value={readOnly.email} />
              <EditableField {...fieldProps} icon={FaPhone} label="Phone Number" name="phone" type="tel" placeholder="10-digit number" />
            </SectionCard>

            {/* Professional — read only */}
            <SectionCard icon={FaBriefcase} title="Professional Information" subtitle="Managed by your administrator">
              <ReadOnlyField
                icon={FaBuilding} label="Company" value={readOnly.company}
                badge={readOnly.companyLogo
                  ? <img src={readOnly.companyLogo} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                  : null}
              />
              <ReadOnlyField
                icon={FaUserTag} label="Designation" value={readOnly.designation}
                badge={readOnly.designationCode
                  ? <span className="text-[10px] font-mono text-text_muted shrink-0">{readOnly.designationCode}</span>
                  : null}
              />
              <ReadOnlyField icon={FaBriefcase} label="Trade of Work" value={readOnly.trade} />
              {readOnly.businessType && (
                <ReadOnlyField icon={FaLayerGroup} label="Business Type" value={readOnly.businessType} />
              )}
              <ReadOnlyField icon={FaShieldAlt} label="Role"          value={readOnly.role}  />
            </SectionCard>

            {/* Address */}
            <SectionCard icon={FaMapMarkerAlt} title="Address Information" subtitle="Your location details">
              <EditableField {...fieldProps} icon={FaMapMarkerAlt} label="Location / Address" name="location" placeholder="Street address" fullWidth />
              <EditableField {...fieldProps} icon={FaCity}         label="City"     name="city" placeholder="City" />
              <ReadOnlyField  icon={FaGlobe}       label="Country"  value={readOnly.country} />
              <EditableField {...fieldProps} icon={FaIdCard}        label="PIN Code" name="pin"  placeholder="Postal code" />
            </SectionCard>

            {/* Save bar — visible only when editing */}
            {isEditing && (
              <div className="flex justify-end gap-2 pt-1 sticky bottom-4 lg:static">
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-text_secondary bg-surface border border-border hover:bg-surface_alt transition-colors shadow-sm disabled:opacity-50"
                >
                  <FaTimes size={13} /> Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-secondary hover:bg-secondary_hover transition-colors shadow-sm disabled:opacity-60"
                >
                  <FaSave size={14} /> {saving ? "Saving…" : "Save All Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
